import { DownloadBookingReceiptButton } from '@components/Booking/DownloadBookingReceiptButton';
import { InitiatePaymentSessionButton } from '@components/Booking/InitiatePaymentSessionButton';
import { BookingConfirmationContent } from '@components/Confirmation/BookingConfirmationContent';
import { BookingConfirmationIntro } from '@components/Confirmation/BookingConfirmationIntro';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { BgImageContainer } from '@components/Layouts/BgImageContainer';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { PageHeading } from '@components/Layouts/PageHeading';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { InviteFriendsTeaser } from '@components/Referrals/InviteFriendsTeaser';
import { CustomerDataQualityProvider } from '@components/UserGroupDataQuality/CustomerDataQualityProvider';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingStatus, PaymentOption } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiDummyButton, NoddiLink } from 'noddi-ui-web';
import { customerRoutes, getIsAwaitingCustomerPayment, getJobCompletedAndPaymentLeft } from 'noddi-util';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useIsPaymentSuccessful } from '../../hooks/useIsPaymentSuccessful';
import { getErrorTextBasedOnErrorType } from '../../utils/dinteroPayment';

const DISPLAY_MS = 2500;
const FADE_MS = 500;

type Phase = 'visible' | 'fading' | 'hidden';

export const PaymentInfo = () => {
  const { slug } = useParams();

  const {
    data: booking,
    isPending: isBookingPending,
    isFetching: isBookingFetching,
    error: bookingError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingInfo,
    input: { slug: slug! }
  });

  const { error, isPaymentSuccessful } = useIsPaymentSuccessful();

  const [phase, setPhase] = useState<Phase>('visible');

  // Payment callback will change status on booking so make sure to also listen to fetching here
  const isBookingLoading = isBookingPending || isBookingFetching;

  // Only start the fade-out timers once the confirmation is actually on screen —
  // otherwise a slow booking fetch burns the timers during the loading screen and
  // the celebration is skipped by the time the confirmed booking renders.
  useEffect(() => {
    if (isBookingLoading) {
      return;
    }
    const t1 = setTimeout(() => setPhase('fading'), DISPLAY_MS);
    const t2 = setTimeout(() => setPhase('hidden'), DISPLAY_MS + FADE_MS);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isBookingLoading]);

  if (isBookingLoading) {
    return <NoddiLoadingScreenWithTrans />;
  }
  if (bookingError) {
    return <ApiErrorPageWithTrans error={bookingError} />;
  }

  const showHappyContent = isPaymentSuccessful || booking.order.isFullyPaid || booking.order.amountGross.amount === 0;
  const isBookingConfirmed =
    booking.status.value === BookingStatus.CONFIRMED ||
    (booking.status.value === BookingStatus.DRAFT_PAY_BEFORE &&
      booking.order.paymentOption === PaymentOption.PAY_BEFORE);

  if (!showHappyContent) {
    // Pay-by-slug only opens a session for the awaiting-payment drafts and COMPLETED. A
    // booking that has moved on (cancelled, confirmed) is refused, so do not offer a retry.
    const canRetryPayment =
      getIsAwaitingCustomerPayment(booking.status.value) || getJobCompletedAndPaymentLeft(booking);

    return (
      // A failed payment is often a detail the provider rejected, so the retry
      // gets the same fix sheet as the first attempt.
      <CustomerDataQualityProvider
        userGroupId={booking.userGroup.id}
        bookingLocationType={booking.locationType}
        brandDomain={booking.serviceDepartmentBrand.domain}
        countryCode={booking.address?.countryCode}
      >
        <BgImageContainer>
          <ContentWrapperCustomerApp hideBackButton className='flex min-h-[60vh] items-center justify-center'>
            <NoddiBasicCard className='flex w-full flex-col items-center gap-4 text-center'>
              <span className='text-2xl font-bold'>
                <Trans>Payment failed</Trans>
              </span>
              <span>{getErrorTextBasedOnErrorType(error)}</span>

              {canRetryPayment ? (
                <InitiatePaymentSessionButton
                  variant='primary'
                  booking={booking}
                  className='w-full'
                  label={<Trans>Try again</Trans>}
                />
              ) : (
                <NoddiLink
                  variant='outline'
                  className='w-full'
                  to={customerRoutes.bookingInfo.getPath({ slug: slug! })}
                >
                  <Trans>View booking</Trans>
                </NoddiLink>
              )}
            </NoddiBasicCard>
          </ContentWrapperCustomerApp>
        </BgImageContainer>
      </CustomerDataQualityProvider>
    );
  }

  if (isBookingConfirmed) {
    const confirmedTitle = t`Booking confirmed and paid!`;
    const heading =
      phase === 'hidden' ? (
        <PageHeading className='text-2xl font-bold text-primary'>{confirmedTitle}</PageHeading>
      ) : (
        <h1 tabIndex={-1} className='text-2xl font-bold text-primary focus:outline-hidden'>
          {confirmedTitle}
        </h1>
      );

    return (
      <>
        {phase !== 'hidden' && (
          <BookingConfirmationIntro
            className={cn(phase === 'visible' ? 'opacity-100' : 'opacity-0')}
            iconClassName={cn(phase === 'visible' ? 'scale-100' : 'scale-95')}
            title={confirmedTitle}
          />
        )}
        <div
          className={cn(
            'flex flex-col gap-5 transition-opacity duration-500',
            phase === 'hidden' ? 'opacity-100' : 'opacity-0'
          )}
          inert={phase !== 'hidden'}
          aria-hidden={phase !== 'hidden'}
        >
          <ContentWrapperCustomerApp hideBackButton>
            <BookingConfirmationContent bookingDetails={booking} heading={heading} />
          </ContentWrapperCustomerApp>
        </div>
      </>
    );
  }

  return (
    <BgImageContainer>
      <ContentWrapperCustomerApp hideBackButton className='flex min-h-[60vh] items-center justify-center'>
        <div className='flex flex-col gap-5 xxs:gap-6 lg:gap-16'>
          <h1 className='text-3xl font-bold text-primary lg:text-4xl'>
            <>
              {booking.order.amountGross.amount === 0 ? (
                <>
                  {/* eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate emoji in customer-facing copy: it carries the tone of the message, it is not a status a NoddiIcon could show */}
                  <Trans>Your booking is free of charge</Trans> 🎉
                </>
              ) : (
                <>
                  {/* eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate emoji in customer-facing copy: it carries the tone of the message, it is not a status a NoddiIcon could show */}
                  <Trans>Thanks, we have received your payment</Trans> 🎉
                </>
              )}
            </>
          </h1>

          {!booking?.eontyreDetail && <DownloadBookingReceiptButton booking={booking} />}

          <div className='flex w-full flex-col gap-5 xxs:gap-6 lg:flex-row lg:gap-16'>
            <NoddiBasicCard
              to={customerRoutes.feedbackForBooking.getPath({ slug: slug! })}
              className='flex w-full flex-col gap-4'
            >
              <h3 className='text-xl font-semibold text-primary'>
                <Trans>How was your experience?</Trans>
              </h3>
              <span>
                <Trans>Help us improve by sharing your thoughts</Trans>
              </span>

              {slug && (
                <div className='flex h-full items-end'>
                  <NoddiDummyButton className='w-full'>
                    <Trans>Give feedback</Trans>
                  </NoddiDummyButton>
                </div>
              )}
            </NoddiBasicCard>
            <InviteFriendsTeaser />
          </div>
        </div>
      </ContentWrapperCustomerApp>
    </BgImageContainer>
  );
};
